import { WriteupState } from '@/store/writeup/state';
import { Mutations } from 'vuex-smart-module';
import {
	DealType,
	IncentiveObject,
	IncentiveRequest,
	PaymentTermsResponse,
	RebateCategory,
	RebateResponse,
	RebateSource,
	WriteupResponse,
	TradeInResponse,
	FiResponse,
	FiTerm,
	UpdateOptionPayload,
	CoverageDropdownItem,
	ScheduleTestDriveData,
	DatesAppointmentData,
	TimeslotsAppointmentData, AppointmentResponseData, PaymentTermsDetails,
	AccessoryResponse, AccessoryRequest,
	TestDriveAppointmentData,
	TaxesAndFees, ShiftDigitalData, GtmData, TealiumResponse,
} from '@/types/writeup';
import {
	TradeInServiceTypeAheadResponse,
	TradeInServiceVinResponse,
	TradeInServiceDetailsResponse,
	TradeIn,
	TradeInCustomerVehicleInputs,
} from '@/types/tradepending';

export class WriteupMutations extends Mutations<WriteupState> {
	updateWriteup(writeup: WriteupResponse) {
		this.state.writeupObject = writeup;
	}

	updateTerms(terms: PaymentTermsResponse) {
		this.state.termsObject = terms;
	}

	updateIncentives(incentives: IncentiveObject[]) {
		this.state.incentives = incentives;
	}

	cleanWriteup() {
		this.state.writeupObject = null;
	}

	cleanTerms() {
		this.state.termsObject = null;
	}

	setError() {
		this.state.isError = true;
	}

	unsetError() {
		this.state.isError = false;
	}

	setDealTypeUpdating() {
		this.state.isDealTypeUpdating = true;
	}

	unsetDealTypeUpdating() {
		this.state.isDealTypeUpdating = false;
	}

	setPendingDealType(type: DealType) {
		this.state.pendingDealType = type;
	}

	unsetPendingDealType() {
		this.state.pendingDealType = null;
	}

	setTermsLoading() {
		this.state.isTermsLoading = true;
	}

	unsetTermsLoading() {
		this.state.isTermsLoading = false;
	}

	setTermUpdating(term: string) {
		if (this.state.isTermsUpdating.includes(term)) { return; }
		this.state.isTermsUpdating.push(term);
	}

	unsetTermUpdating(term: string) {
		this.state.isTermsUpdating = this.state.isTermsUpdating.filter(item => term !== item);
	}

	unsetTermsUpdating() {
		this.state.isTermsUpdating = [];
	}

	setWriteupUpdating() {
		this.state.isWriteupUpdating = true;
	}

	unsetWriteupUpdating() {
		this.state.isWriteupUpdating = false;
	}

	setTradeInUpdating() {
		this.state.isTradeInUpdating = true;
	}

	unsetTradeInUpdating() {
		this.state.isTradeInUpdating = false;
	}

	updateTradeIn(tradeIn: TradeInResponse) {
		this.state.tradeInObject = tradeIn;
	}

	updateFiObjects(fiObjects: Array<FiResponse>) {
		this.state.fiObjects = fiObjects;
	}

	updateOptions(payload: FiResponse) {
		//TODO: if we decide to refactor fiobject data structure
		const value: Array<FiResponse> = [];

		value[payload.type] = payload;
		this.state.options[payload.id] = value;
	}

	selectOption(payload: UpdateOptionPayload) {
		this.state.fiObjects = this.state.fiObjects.map(option => {
			const selectedOption = option;

			if (selectedOption.id === payload.optionId && selectedOption.type === payload.type) {
				selectedOption.selected = !selectedOption.selected;
			}

			return selectedOption;
		});
	}

	addOption(payload: UpdateOptionPayload) {
		this.state.fiObjects = this.state.fiObjects.map(option => {
			const selectedOption = option;

			if (selectedOption.id === payload.optionId && selectedOption.type === payload.type) {
				if (Array.isArray(this.state.allCoverages) && !this.state.allCoverages.length) {
					selectedOption.selected = true;
				} else {
					selectedOption.selected = true;
					selectedOption.term = {
						price: payload.price,
						months: payload.months,
						miles: payload.miles,
						coverage: payload.selectedCoverage,
						deductible: payload.selectedDeductible,
					};
				}
			}

			return selectedOption;
		});
	}

	setFiObjectUpdating() {
		this.state.isFiObjectUpdating = true;
	}

	unsetFiObjectUpdating() {
		this.state.isFiObjectUpdating = false;
	}

	allCoveragesDropdownList(payload: { optionId: number|undefined; coverages: Array<CoverageDropdownItem>}) {
		this.state.allCoverages.push(payload);
	}

	setFiTerms(payload: { optionId: number|undefined; fiTermsResponse: Array<FiTerm> }) {
		this.state.termList.push(payload);

		const array: Array<CoverageDropdownItem> = [];
		const termList = (this.state.termList.filter(term => term.optionId === payload.optionId));

		termList[0].fiTermsResponse.forEach(term => {
			const itemIndex = array.findIndex(element => element.value === term.coverage);
			const miles = term.miles ? ` / ${new Intl.NumberFormat('en-US').format(term.miles)}` : '';
			const termString = `${term.months} months${miles}`;
			const deductible = term.deductible ? term.deductible : 'None';

			if (itemIndex !== -1) {
				const deductibleIndex = array[itemIndex].deductible.findIndex(el => el.value === deductible);

				if (deductibleIndex !== -1) {
					array[itemIndex].deductible[deductibleIndex].terms.push({
						label: termString,
						value: termString,
						months: term.months,
						price: term.price,
						miles: term.miles,
					});
				} else {
					array[itemIndex].deductible.push({
						label: deductible,
						value: deductible,
						terms: [{
							label: termString,
							value: termString,
							months: term.months,
							price: term.price,
							miles: term.miles,
						}],
					});
				}
			} else {
				array.push({
					label: term.coverage,
					value: term.coverage,
					deductible: [{
						label: deductible,
						value: deductible,
						terms: [{
							label: termString,
							value: termString,
							months: term.months,
							price: term.price,
							miles: term.miles,
						}],
					}],
				});
			}
		});

		const coveragesObject = { optionId: payload.optionId, coverages: array };

		this.state.allCoverages.push(coveragesObject);
	}

	setPriceToFIObject(payload: {optionId: number; price: number}) {
		for (let i = 0; i < this.state.fiObjects.length; i += 1) {
			if (this.state.fiObjects[i].id === payload.optionId && (!this.state.fiObjects[i].price)) {
				this.state.fiObjects[i].price = payload.price;
				break;
			}
		}
	}

	setRebatesUpdating() {
		this.state.isRebatesUpdating = true;
	}

	unsetRebatesUpdating() {
		this.state.isRebatesUpdating = false;
	}

	setRebatesSettingsApplied() {
		this.state.isRebatesSettingsApplied = true;
	}

	unsetRebatesSettingsApplied() {
		this.state.isRebatesSettingsApplied = false;
	}

	updateRebates(rebates: RebateResponse[]) {
		this.state.rebates = rebates.map((rebate, id) => {
			const mockedRebate = { ...rebate };

			if (rebate?.name?.toLowerCase().includes('college')) { mockedRebate.category = RebateCategory.College; }
			if (rebate?.name?.toLowerCase().includes('loyalty')) { mockedRebate.category = RebateCategory.Loyalty; }
			if (rebate?.name?.toLowerCase().includes('military')) { mockedRebate.category = RebateCategory.Military; }

			return mockedRebate;
		});
	}

	updateVehicleList(vehicleList: TradeInServiceTypeAheadResponse[]) {
		this.state.vehicleList = vehicleList;
	}

	setFetchingVehicleList() {
		this.state.isVehiclesFetching = true;
	}

	unsetFetchingVehicleList() {
		this.state.isVehiclesFetching = false;
	}

	updateVehicleDetails(vehicleDetails: TradeInServiceDetailsResponse) {
		this.state.vehicleDetails = vehicleDetails;
	}

	setFetchingVehicleDetails() {
		this.state.isVehiclesDetailsFetching = true;
	}

	unsetFetchingVehicleDetails() {
		this.state.isVehiclesDetailsFetching = false;
	}

	resetSuggestions() {
		this.state.vehicleList = [];
	}

	setVehicleSuggestionError() {
		this.state.isVehicleSuggestionError = true;
	}

	unsetVehicleSuggestionError() {
		this.state.isVehicleSuggestionError = false;
	}

	setIncentivesSaving() {
		this.state.isIncentivesSaving = true;
	}

	unsetIncentivesSaving() {
		this.state.isIncentivesSaving = false;
	}

	setLastIncentiveRequest(request: IncentiveRequest | null) {
		this.state.lastIncentiveRequest = request;
	}

	setDatesAppointmentUpdating() {
		this.state.isDatesAppointmentUpdating = true;
	}

	unsetDatesAppointmentUpdating() {
		this.state.isDatesAppointmentUpdating = false;
	}

	setTimeslotsAppointmentUpdating() {
		this.state.isTimeslotsAppointmentUpdating = true;
	}

	unsetTimeslotsAppointmentUpdating() {
		this.state.isTimeslotsAppointmentUpdating = false;
	}

	saveAppointmentRequestData(appointmentRequest: AppointmentResponseData) {
		this.state.appointmentResponseData = appointmentRequest;
	}

	saveScheduleTestDriveData(scheduleTestDriveData: ScheduleTestDriveData) {
		this.state.scheduleTestDriveData = scheduleTestDriveData;
	}

	unsetScheduledDriveStatus() {
		this.state.scheduleTestDriveData = null;
	}

	updateDatesAppointment(dates: DatesAppointmentData) {
		this.state.datesAppointment = dates;
	}

	updateTimeslotsAppointment(timeslots: TimeslotsAppointmentData) {
		this.state.timeslotsAppointment = timeslots;
	}

	updateTestDriveAppointmentData(testDriveAppointmentData: TestDriveAppointmentData) {
		this.state.testDriveAppointmentData = testDriveAppointmentData;
	}

	setTradeInEnterVehicleInputs(request: TradeInCustomerVehicleInputs) {
		this.state.tradeInEnterVehicleInputs = request;
	}

	setTradeInReportupdating() {
		this.state.isTradeInReportUpdating = true;
	}

	unsetTradeInReportupdating() {
		this.state.isTradeInReportUpdating = false;
	}

	updateTradeInReport(report: any) {
		this.state.tradeInReport = report;
	}

	updateTaxesAndFees(data: TaxesAndFees) {
		this.state.taxesAndFees = data;
	}

	updateOptionProvider(optionProvider: string) {
		this.state.optionProvider = optionProvider;
	}

	updatePaymentTermsDetails(termDetails: Partial<PaymentTermsDetails>) {
		this.state.paymentTermsDetails = termDetails;
	}

	setAccessoryObjectUpdating() {
		this.state.isAccessoryUpdating = true;
	}

	unsetAccessoryObjectUpdating() {
		this.state.isAccessoryUpdating = false;
	}

	updateAccessoryObjects(accessoryObjects: Array<AccessoryResponse>) {
		this.state.accessories = accessoryObjects;
	}

	selectAccessory(payload: AccessoryRequest) {
		this.state.accessories = this.state.accessories.map((accessory: AccessoryResponse) => {
			const selectedAccessory = accessory;

			if (selectedAccessory.id === payload.id) {
				selectedAccessory.selected = !selectedAccessory.selected;
			}

			return selectedAccessory;
		});
	}

	setShiftDigitalData(shiftDigitalData: Partial<ShiftDigitalData>) {
		this.state.shiftDigitalData = shiftDigitalData;
	}

	setGtmData(gtmData: Partial<GtmData>) {
		this.state.gtmData = gtmData;
	}

	setTealiumData(tealiumData: Partial<TealiumResponse>) {
		this.state.tealiumData = tealiumData;
	}

	setPpLoaded() {
		this.state.isPpLoaded = true;
	}

	unSetPpLoaded() {
		this.state.isPpLoaded = false;
	}

	setLoading() {
		this.state.isLoading = true;
	}

	unSetLoading() {
		this.state.isLoading = false;
	}

	setIsBusy(status: boolean) {
		this.state.isBusy = status;
	}
}
